import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, CheckCircle2, Bell, ExternalLink, X, Coffee } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { showAppNotification } from '../lib/notifications';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';

interface AlertItem {
  id: string;
  title: string;
  body: string;
  url?: string;
  timestamp: number;
}

export function RealtimeAlertWatcher() {
  const [activeAlert, setActiveAlert] = useState<AlertItem | null>(null);
  const seenAlertsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;

    const checkAlerts = async () => {
      try {
        const token = localStorage.getItem('pulse_token');
        if (!token) return;

        if (typeof document !== 'undefined' && document.hidden) {
          return; // Pause requests when tab is hidden to save Cloudflare quota
        }

        const res = await api.admin.getLatestAlert();
        if (!isMounted || !res || !res.title) return;

        // Skip default idle message
        if (res.title.includes('System Update') || res.body.includes('currently active')) {
          return;
        }

        const alertKey = `${res.title}_${res.body}`;
        if (seenAlertsRef.current.has(alertKey)) {
          return;
        }

        // Check if already acknowledged in this session
        const acked = sessionStorage.getItem(`acked_alert_${alertKey}`);
        if (acked) {
          seenAlertsRef.current.add(alertKey);
          return;
        }

        seenAlertsRef.current.add(alertKey);

        const newAlert: AlertItem = {
          id: alertKey,
          title: res.title,
          body: res.body,
          url: res.url || '/admin/live',
          timestamp: Date.now(),
        };

        setActiveAlert(newAlert);
        haptics.alert();
        sounds.playAlertPing();

        // Fire native notification & sound chime
        showAppNotification({
          title: res.title,
          body: res.body,
          url: res.url || '/admin/live',
          tag: 'ckpulse-realtime',
        });
      } catch (err) {
        // Polling failed or user logged out
      }
    };

    // Initial check after 3 seconds
    const initialTimer = setTimeout(checkAlerts, 3000);

    // Poll every 25 seconds (relaxed for Cloudflare quota optimization)
    const interval = setInterval(checkAlerts, 25000);

    const handleVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        checkAlerts();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isMounted = false;
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleDismiss = () => {
    haptics.tap();
    sounds.playTapSound();
    if (activeAlert) {
      sessionStorage.setItem(`acked_alert_${activeAlert.id}`, 'true');
    }
    setActiveAlert(null);
  };

  if (!activeAlert) return null;

  const isCoffee = activeAlert.title.includes('Break');
  const isOk = activeAlert.title.includes('Resumed');

  return (
    <aside
      aria-label="Realtime Inactivity Alert"
      className="fixed top-[max(calc(env(safe-area-inset-top,0px)+3.75rem),4.25rem)] inset-x-3.5 max-w-lg mx-auto z-50 pointer-events-auto select-none"
    >
      <div className="rounded-2xl bg-zinc-950/95 border border-amber-500/40 p-3.5 shadow-2xl backdrop-blur-xl flex items-start gap-3 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-pulse-blue to-emerald-500" />

        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
            isCoffee
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : isOk
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          }`}
        >
          {isCoffee ? (
            <Coffee className="w-4 h-4" />
          ) : isOk ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
        </div>

        <div className="flex-1 min-w-0 pr-7">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-bold text-white tracking-tight truncate">{activeAlert.title}</h4>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30 shrink-0">
              ALERT
            </span>
          </div>
          <p className="text-xs text-zinc-300 mt-1 leading-snug break-words">{activeAlert.body}</p>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Link
              to={activeAlert.url || '/admin/live'}
              onClick={handleDismiss}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl bg-amber-500 hover:bg-amber-400 text-black shadow-md shadow-amber-500/20 ios-press shrink-0"
            >
              <span>View Monitor</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-semibold text-zinc-300 hover:text-white rounded-xl bg-zinc-800/80 hover:bg-zinc-700 border border-white/10 ios-press shrink-0"
            >
              Acknowledge
            </button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-6 h-6 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white flex items-center justify-center border border-white/10 ios-press"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </aside>
  );
}

